// crewbit-admin/src/components/money/settings/ExportMoneyDataCard.tsx
//
// Export Money data card. Per Decision §Settings.D6 + §3h LOCKED:
//   - export types in v1: t2125 / hst_summary
//   - payouts_log HIDDEN v1 per Hard Rule #7 (gated on §Payouts.PostLaunch
//     substrate; filed §Settings.ExportPayoutsLog deferred).
//
// Each row: label + description + Download button. Click → useDownloadMoneyExport
// → browser triggers Blob+<a download> click. On rate-limit → warning toast
// with edge fn message.
//
// Mirrors crewbit-mobile/components/money/settings/ExportMoneyDataCard.tsx.

import { useState } from 'react';
import { Download } from 'lucide-react';
import { palette, radii, spacing, typography } from '../../../styles/tokens';
import {
  useDownloadMoneyExport,
  type MoneyExportType,
} from '../../../hooks/useDownloadMoneyExport';
import { lookupErrorCopy } from '../../../lib/errorMap';
import { Toast } from '../../common/Toast';

interface ExportRow {
  exportType: MoneyExportType;
  label: string;
  description: string;
}

const ROWS: ExportRow[] = [
  {
    exportType: 't2125',
    label: 'T2125 (CSV)',
    description: 'Self-employment business income statement — categorized expenses + revenue, ready for tax filing.',
  },
  {
    exportType: 'hst_summary',
    label: 'HST/GST summary (CSV)',
    description: 'Quarterly HST collected, ITCs claimed, net owing — by remittance period.',
  },
  // payouts_log hidden v1 per Hard Rule #7
];

interface ExportMoneyDataCardProps {
  businessId: string | null;
}

export default function ExportMoneyDataCard({ businessId }: ExportMoneyDataCardProps) {
  const downloadExport = useDownloadMoneyExport();
  const [busyType, setBusyType] = useState<MoneyExportType | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);

  const handleExport = async (row: ExportRow) => {
    if (!businessId || busyType) return;
    setBusyType(row.exportType);
    const result = await downloadExport({ businessId, exportType: row.exportType });
    setBusyType(null);
    if (result.success) {
      setToast({ message: `Downloaded ${row.label}.`, type: 'success' });
    } else {
      const copy =
        result.error.message ??
        lookupErrorCopy('generate_money_export', result.error.code as Parameters<typeof lookupErrorCopy>[1]);
      setToast({ message: copy, type: 'warning' });
    }
  };

  return (
    <>
      <div style={{
        background: palette.white,
        borderRadius: radii.lg,
        border: `1px solid ${palette.g150}`,
      }}>
        <div style={{
          paddingInline: spacing.md, paddingTop: spacing.md, paddingBottom: spacing.sm,
          display: 'flex', flexDirection: 'column', gap: spacing.xxs,
        }}>
          <div style={{ fontSize: typography.size.heading, fontWeight: 700, color: palette.g800 }}>
            Export your data
          </div>
          <div style={{ fontSize: typography.size.caption, color: palette.g500 }}>
            CSV exports for tax filing and accountant handoff. More export
            formats land alongside payouts and team payroll.
          </div>
        </div>
        {ROWS.map((row, idx) => {
          const isLast = idx === ROWS.length - 1;
          const busy = busyType === row.exportType;
          const disabled = !businessId || busyType !== null;
          return (
            <div
              key={row.exportType}
              style={{
                display: 'flex', alignItems: 'center', gap: spacing.sm,
                paddingInline: spacing.md, paddingBlock: spacing.md,
                borderBottom: isLast ? 'none' : `1px solid ${palette.g150}`,
              }}
            >
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: spacing.xxs, minWidth: 0 }}>
                <span style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
                  {row.label}
                </span>
                <span style={{ fontSize: typography.size.caption, color: palette.g500, lineHeight: 1.4 }}>
                  {row.description}
                </span>
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={() => { void handleExport(row); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  paddingInline: spacing.md, paddingBlock: spacing.sm,
                  borderRadius: radii.sm,
                  border: 'none',
                  background: disabled ? palette.g300 : palette.accent,
                  color: palette.white,
                  fontSize: typography.size.caption,
                  fontWeight: 700,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  flexShrink: 0,
                }}
              >
                <Download size={14} color={palette.white} strokeWidth={2.5} />
                {busy ? 'Exporting…' : 'Export'}
              </button>
            </div>
          );
        })}
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}
